<script setup lang="ts">
  import { inject, ref } from 'vue';
  import { AlcAlert, AlcButton, AlcInput, ButtonVariantEnum } from '@alchemer/vue-component-library';
  import { useDebounceFn } from '@vueuse/core';

  import { fetchAllCheckAlias } from '@/api/';
  import { LoginTypeEnum } from '@/types/enums';
  import { ChangeLoginTypeKey } from '@/types/symbols';
  import { EnvironmentalConfigKey } from '@/utils/environmental-config';

  const environmentalConfig = inject(EnvironmentalConfigKey);
  const changeLogin = inject(ChangeLoginTypeKey, () => {});
  const formAction = ref('');
  const formRef = ref<HTMLFormElement>();
  const isVerifying = ref<boolean>(false);
  const ssoAlias = ref<string | undefined>('');

  const warningMessage = ref<string | undefined>('');

  const handleSsoChange = (value: string) => {
    if (ssoAlias.value?.trim().length === 0) {
      warningMessage.value = '';
    }
    ssoAlias.value = value;
  };

  const handleBackClick = () => {
    changeLogin(LoginTypeEnum.EMAIL);
  };

  const handleSubmitForm = () => {
    if (formAction.value !== '') {
      formRef.value?.submit();
    }
  };

  const handleHelpClick = () => {
    window.open('https://help.alchemer.com/help/sso#account-alias', '_blank');
  };

  const handleContinueClick = useDebounceFn(async () => {
    if (!ssoAlias.value || ssoAlias.value?.trim().length === 0) {
      warningMessage.value = 'Please enter a valid account alias.';
      return;
    }

    try {
      if (environmentalConfig) {
        const datacenterKeys = environmentalConfig.getDatacenterPriority();

        // gets responses from all datacenters
        const results = await fetchAllCheckAlias(ssoAlias.value, datacenterKeys, environmentalConfig);

        for (let i = 0; i < results.length; i++) {
          let response = results[i];

          if (response.status !== 'fulfilled') {
            return;
          }

          const data = await response.value.json();

          if (data?.success && typeof data.redirect === 'string') {
            // Redirect to the data.redirect URL
            window.location.href = data.redirect;
            return; // Exit the loop and function after redirecting
          }
        }
        warningMessage.value = `No alias found.  Please check your entry and try again.`;
        return;
      }
    } catch (error) {
      warningMessage.value = `There was a problem with this alias.  Please check your entry and try again.`;
    } finally {
      isVerifying.value = false;
    }
  }, 250);
</script>
<template>
  <form
    ref="formRef"
    @submit.prevent="handleSubmitForm"
    class="login-sso"
    method="post"
    :action="formAction"
  >
    <div
      class="login-sso__back"
      @click="handleBackClick"
    >
      <FontAwesomeIcon icon="angle-left" />
      Back
    </div>
    <div>
      <label
        class="login-sso__back"
        for="ssoAlias"
        >Account Alias</label
      >
      <AlcInput
        id="ssoAlias"
        name="ssoAlias"
        :default-value="ssoAlias"
        placeholder="Enter your account alias"
        @input-changed="handleSsoChange"
        @input-reset="handleSsoChange('')"
      />
    </div>
    <div
      class="login-sso__warning"
      v-show="warningMessage && warningMessage.trim().length > 0"
    >
      <AlcAlert>{{ warningMessage }}</AlcAlert>
    </div>

    <div>
      <AlcButton
        :stretch="true"
        label="Continue"
        :disabled="isVerifying"
        @alc-btn-click="handleContinueClick"
      />
    </div>
    <div>
      <AlcButton
        :stretch="true"
        :variant="ButtonVariantEnum.SECONDARY"
        label="Need help?"
        @alc-btn-click="handleHelpClick"
      />
    </div>
  </form>
</template>

<style lang="scss" scoped>
  .login-sso {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    color: var(--alc-base-800);

    &__back {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 20px;

      &:hover {
        cursor: pointer;
      }
    }

    &__label {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 15.4px;
    }
  }
</style>
